if (!customElements.get('carousel-slider')) {
	customElements.define('carousel-slider', class CarouselSlider extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.carousel = this.querySelector('.grid-carousel');
			this.initialize();
		}

		initialize() {
			// Flickity doesn't work with flex containers, so
			// to avoid flash of unstyled content and page jump, style the container
			// as block just before initialization. On mobile where the carousel
			// is converted to simple scroller we swap this to display: flex !important
			// using CSS.
			this.carousel.style.display = 'block';
			this.flickity = new Flickity(this.carousel, {
				cellAlign: 'left',
				groupCells: true,
				contain: true,
				resize: true,
				draggable: true,
				prevNextButtons: false,
				fade: false,
				cellSelector: '.grid-item',
				initialIndex: 0,
				pageDots: false,
				wrapAround: false,
				accessibility: false,
				watchCSS: true,
			});

			this.flickity.on('change', (index) => {
				const event = new CustomEvent('slider:slide-change', {
					detail: {
						id: this.getAttribute('id'),
						index,
					},
				});
				document.dispatchEvent(event);
			});
		}
	});
}

if (!customElements.get('carousel-navigation')) {
	customElements.define('carousel-navigation', class CarouselNavigation extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.init();
		}

		init(retry = 1, maxRetry = 3) {
			if (retry === maxRetry) {
				return;
			}

			const id = this.getAttribute('for');
			this.carouselComponent = document.querySelector(`#${id}`);
			this.wrap = this.dataset.wrap === 'true';

			if ((!this.carouselComponent || !this.carouselComponent.flickity)) {
				setTimeout(() => {
					this.init(retry + 1);
				}, 350);
				return;
			}

			this.flickity = this.carouselComponent.flickity;
			this.buttonPrev = this.querySelector('.js-prev');
			this.buttonNext = this.querySelector('.js-next');

			this.buttonPrev.addEventListener('click', (event) => {
				event.preventDefault();
				this.flickity.previous();
			});

			this.buttonNext.addEventListener('click', (event) => {
				event.preventDefault();
				this.flickity.next();
			});

			this.flickity.on('select', () => {
				if (this.wrap) {
					return;
				}

				this.enableButton(this.buttonPrev);
				this.enableButton(this.buttonNext);

				if (!this.flickity.slides[this.flickity.selectedIndex - 1]) {
					this.disableButton(this.buttonPrev);
				} else if (!this.flickity.slides[this.flickity.selectedIndex + 1]) {
					this.disableButton(this.buttonNext);
				}
			});
		}

		enableButton(button) {
			button.classList.remove('pill-nav-item-disabled');
			button.classList.remove('focus-none');
			button.setAttribute('aria-disabled', 'false');
		}

		disableButton(button) {
			button.classList.add('pill-nav-item-disabled');
			button.classList.add('focus-none');
			button.setAttribute('aria-disabled', 'true');
		}
	});
}

if (!customElements.get('scroller-navigation')) {
	customElements.define('scroller-navigation', class ScrollerNavigation extends HTMLElement {
		connectedCallback() {
			this.init();
		}

		init(retry = 1, maxRetry = 5) {
			const id = this.getAttribute('for');
			this.scrollerTarget = document.getElementById(id);
			this.scrollArea = this.scrollerTarget?.querySelector('.promo-carousel__scrollport')
				|| this.scrollerTarget?.querySelector('.scroller-inner');
			this.ltr = !document.querySelector('html[dir=rtl]');

			if (!this.scrollArea) {
				if (retry < maxRetry) {
					setTimeout(() => {
						this.init(retry + 1);
					}, 350);
				}
				return;
			}

			this.buttonPrev = this.querySelector('.js-prev');
			this.buttonNext = this.querySelector('.js-next');

			this.buttonPrev?.addEventListener('click', (event) => {
				event.preventDefault();
				this.scrollByPage(-1);
			});

			this.buttonNext?.addEventListener('click', (event) => {
				event.preventDefault();
				this.scrollByPage(1);
			});

			this.onScroll = () => this.updateButtons();
			this.onResize = () => this.updateButtons();
			this.scrollArea.addEventListener('scroll', this.onScroll);
			window.addEventListener('resize', this.onResize);
			this.updateButtons();
		}

		disconnectedCallback() {
			this.scrollArea?.removeEventListener('scroll', this.onScroll);
			window.removeEventListener('resize', this.onResize);
		}

		getScrollLeft() {
			return Math.abs(this.scrollArea.scrollLeft);
		}

		scrollByPage(direction) {
			const slide = this.scrollArea.querySelector('.grid-item');
			const gap = parseFloat(getComputedStyle(this.scrollArea).columnGap) || 0;
			const slideDistance = (slide?.offsetWidth || this.scrollArea.clientWidth * 0.85) + gap;
			const distance = slideDistance * direction;
			const nextScroll = this.getScrollLeft() + distance;

			this.scrollArea.scrollTo({
				left: this.ltr ? nextScroll : -nextScroll,
				behavior: 'smooth',
			});
		}

		updateButtons() {
			if (!this.scrollArea) {
				return;
			}

			const scrollLeft = this.getScrollLeft();
			const atStart = scrollLeft <= 1;
			const atEnd = scrollLeft + this.scrollArea.clientWidth + 2 >= this.scrollArea.scrollWidth;

			if (atStart) {
				this.disableButton(this.buttonPrev);
			} else {
				this.enableButton(this.buttonPrev);
			}

			if (atEnd) {
				this.disableButton(this.buttonNext);
			} else {
				this.enableButton(this.buttonNext);
			}
		}

		enableButton(button) {
			if (!button) {
				return;
			}

			button.classList.remove('pill-nav-item-disabled');
			button.classList.remove('focus-none');
			button.setAttribute('aria-disabled', 'false');
		}

		disableButton(button) {
			if (!button) {
				return;
			}

			button.classList.add('pill-nav-item-disabled');
			button.classList.add('focus-none');
			button.setAttribute('aria-disabled', 'true');
		}
	});
}

